import { forwardRef, PropsWithChildren } from "react";
import { type PropsWithOptionalTestID } from "@boi/components-core";
import { useDesignTokens } from "@boi/react-design-tokens";

import * as S from "./Fieldset.styled";
import { Typography } from "../Typography";
import { AlertAssociationProps } from "../Alert/AlertAssociationProps";
import {
  InputLabelTopTooltip,
  type InputLabelTopTooltipProps,
} from "../InputLabelTopTooltip";

export type FieldsetProps = PropsWithOptionalTestID &
  PropsWithChildren & {
    legend?: string;
    legendTooltipProps?: Omit<
      InputLabelTopTooltipProps,
      "otherIconButtonProps"
    >;
    legendHint?: string;
    legendHintTooltipProps?: Omit<
      InputLabelTopTooltipProps,
      "otherIconButtonProps"
    >;
    ariaAlertAssociationProps: AlertAssociationProps;
  };

const Fieldset = forwardRef<HTMLFieldSetElement, FieldsetProps>((props, ref) => {
  const {
    legend,
    legendTooltipProps,
    legendHint,
    legendHintTooltipProps,
    ariaAlertAssociationProps,
    testID = "fieldset",
    children,
  } = props;

  const { tokens } = useDesignTokens();
  const { fieldset } = tokens;

  return (
    <S.Fieldset ref={ref} data-testid={testID} {...ariaAlertAssociationProps}>
      {legend && (
        <S.Legend
          color={fieldset.legend.color}
          variant={fieldset.legend.typography.variant}
          fontFamily={fieldset.legend.typography.fontFamily}
        >
          {legend}

          {legendTooltipProps && (
            <InputLabelTopTooltip
              {...legendTooltipProps}
              otherIconButtonProps={{
                variant: "default",
                typographyVariant: fieldset.legendHint.typography.variant,
              }}
            />
          )}
        </S.Legend>
      )}

      {legend && !legendHint && <div />}

      {legendHint && (
        <div>
          <Typography
            component="span"
            color={fieldset.legendHint.color}
            variant={fieldset.legendHint.typography.variant}
            fontFamily={fieldset.legendHint.typography.fontFamily}
          >
            {legendHint}

            {legendHintTooltipProps && (
              <InputLabelTopTooltip
                {...legendHintTooltipProps}
                otherIconButtonProps={{
                  variant: "default20",
                  typographyVariant: fieldset.legendHint.typography.variant,
                }}
              />
            )}
          </Typography>
        </div>
      )}

      {children}
    </S.Fieldset>
  );
});

Fieldset.displayName = "Fieldset"

export default Fieldset;
